// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import config from "@config";
import actionCreators from "@modules/employee/actionCreator";

import {
  GetEmployeeMedications,
  SetEmployeeMedicationSort,
  AddMedication,
  UpdateMedication,
  RemoveMedication,
  GetEmployeeAllergies,
  SetEmployeeAllergySort,
  AddAllergy,
  UpdateAllergy,
  RemoveAllergy,
  GetEmployeeMedicalHistory,
  SetEmployeeMedicalHistorySort,
  AddMedicalHistoryItem,
  RemoveMedicalHistoryItem,
  GetMedicalDetailsChangelog,
  GetVaccineTypes,
  GetEmployeeVaccinations,
  AddEmployeeVaccination,
  UpdateEmployeeVaccination,
  DeleteEmployeeVaccination,
} from "@modules/employee/constants/actions";
import { sortToString } from "@transformers/sort";
import {
  getEmployeeMedicationSort,
  getEmployeeAllergySort,
  getEmployeeMedicalHistorySort,
  getChangelogSort,
} from "@modules/employee/selectors";
import type { PaginationType, ChangelogType } from "@types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import type { Medications, Allergy, MedicalHistory } from "../reducer/medical-details/types";
import { parseSchema } from "@utils/schema";

type EmployeeMedicationsRequest = $Exact<{
  clientId: string,
  employeeId: string,
  page?: number,
}>;

type EmployeeMedicationsAddRequest = $Exact<{
  formData: Object,
  ...EmployeeMedicationsRequest,
}>;

type EmployeeMedicationsUpdateRequest = $Exact<{
  data: Object,
  medicationDetailsId: number,
  ...EmployeeMedicationsRequest,
}>;

type EmployeeAllergyUpdateRequest = $Exact<{
  data: Object,
  allergyDetailsId: number,
  ...EmployeeMedicationsRequest,
}>;

type EmployeeMedicationsDeleteRequest = $Exact<{
  medicationId: number,
  ...EmployeeMedicationsRequest,
}>;

type EmployeeAllergyDeleteRequest = $Exact<{
  allergyId: number,
  ...EmployeeMedicationsRequest,
}>;

type EmployeeMedicalHistoryDeleteRequest = $Exact<{
  medicalHistoryItemId: number,
  ...EmployeeMedicationsRequest,
}>;

export function* onGetEmployeeMedications({
  payload,
}: Action<EmployeeMedicationsRequest>): Generator<
  *,
  void,
  $Exact<{
    data: {
      content: Medications[],
      pagination: PaginationType,
      totalElements: number,
    },
  }>,
> {
  try {
    if (!payload || !payload.employeeId || !payload.clientId) {
      throw Error("Invalid payload provided");
    }
    const page = payload.page || 1;
    const sort = yield select(getEmployeeMedicationSort);

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/medication-details`,
      {
        params: {
          page,
          limit: config.shortResultsPerPage,
          sort: sortToString(sort),
        },
      },
    );
    const schema = yield parseSchema("MedicationDetailsDTO");
    yield put(actionCreators.getEmployeeMedicationsSuccess(response.data));
    yield put(actionCreators.setMedicationSchema(schema));
  } catch (e) {
    yield put(actionCreators.getEmployeeMedicationsError(e.response));
  }
}

export function* onAddMedication({
  payload,
}: Action<EmployeeMedicationsAddRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.employeeId || !payload.clientId || !payload.formData) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/medication-details`,
      payload.formData,
    );
    yield all([
      put(actionCreators.toggleAddMedicationModal(false)),
      put(
        actionCreators.getEmployeeMedications({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(
        actionCreators.getMedicalDetailsChangelog({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(actionCreators.AddMedicationSuccess()),
      put(sendSuccessMessage("MEDICATION_ADDED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddMedicationError(e.response));
  }
}

export function* onUpdateMedication({
  payload,
}: Action<EmployeeMedicationsUpdateRequest>): Generator<*, void, void> {
  try {
    const clientId = (payload || {}).clientId || null;
    const employeeId = (payload || {}).employeeId || null;
    const medicationDetailsId = (payload || {}).medicationDetailsId || null;
    const data = (payload || {}).data || {};

    if (!employeeId || !clientId || !medicationDetailsId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.put,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/medication-details/${medicationDetailsId}`,
      data,
    );
    yield all([
      put(actionCreators.UpdateMedicationSuccess()),
      put(
        actionCreators.getEmployeeMedications({
          clientId,
          employeeId,
        }),
      ),
      put(
        actionCreators.getMedicalDetailsChangelog({
          clientId,
          employeeId,
        }),
      ),
      put(sendSuccessMessage("MEDICATION_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateMedicationError(e.response));
  }
}

export function* onRemoveMedication({
  payload,
}: Action<EmployeeMedicationsDeleteRequest>): Generator<*, void, void> {
  try {
    const clientId = (payload || {}).clientId || null;
    const employeeId = (payload || {}).employeeId || null;
    const medicationDetailsId = (payload || {}).medicationId || null;

    if (!employeeId || !clientId || !medicationDetailsId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/medication-details/${medicationDetailsId}`,
    );
    yield all([
      put(actionCreators.toggleRemoveMedicationModal({ visible: false })),
      put(
        actionCreators.getEmployeeMedications({
          clientId,
          employeeId,
        }),
      ),
      put(
        actionCreators.getMedicalDetailsChangelog({
          clientId,
          employeeId,
        }),
      ),
      put(actionCreators.RemoveMedicationSuccess()),
      put(sendSuccessMessage("MEDICATION_REMOVED")),
    ]);
  } catch (e) {
    yield put(actionCreators.RemoveMedicationError(e.response));
  }
}

export function* onGetEmployeeAllergies({
  payload,
}: Action<EmployeeMedicationsRequest>): Generator<
  *,
  void,
  $Exact<{
    data: {
      content: Allergy[],
      pagination: PaginationType,
      totalElements: number,
    },
  }>,
> {
  try {
    if (!payload || !payload.employeeId || !payload.clientId) {
      throw Error("Invalid payload provided");
    }
    const page = (payload || {}).page || 1;
    const sort = yield select(getEmployeeAllergySort);

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/allergy-details`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
          sort: sortToString(sort),
        },
      },
    );
    const schema = yield parseSchema("AllergyDetailsDTO");

    yield put(actionCreators.getEmployeeAllergiesSuccess(response.data));
    yield put(actionCreators.setAllergySchema(schema));
  } catch (e) {
    yield put(actionCreators.getEmployeeAllergiesError(e.response));
  }
}

export function* onAddAllergy({
  payload,
}: Action<EmployeeMedicationsAddRequest>): Generator<*, void, void> {
  try {
    const clientId = (payload || {}).clientId;
    const employeeId = (payload || {}).employeeId;
    const data = (payload || {}).formData || {};

    if (!employeeId || !clientId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/allergy-details`,
      data,
    );
    yield all([
      put(actionCreators.toggleAddAllergyModal(false)),
      put(
        actionCreators.getEmployeeAllergies({
          clientId,
          employeeId,
        }),
      ),
      put(
        actionCreators.getMedicalDetailsChangelog({
          clientId,
          employeeId,
        }),
      ),
      put(actionCreators.AddAllergySuccess()),
      put(sendSuccessMessage("ALLERGY_ADDED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddAllergyError(e.response));
  }
}

export function* onUpdateAllergy({
  payload,
}: Action<EmployeeAllergyUpdateRequest>): Generator<*, void, void> {
  try {
    const clientId = (payload || {}).clientId;
    const employeeId = (payload || {}).employeeId;
    const allergyDetailsId = (payload || {}).allergyDetailsId;
    const data = (payload || {}).data || {};

    if (!employeeId || !clientId || !allergyDetailsId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.put,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/allergy-details/${allergyDetailsId}`,
      data,
    );
    yield all([
      put(actionCreators.UpdateAllergySuccess()),
      put(
        actionCreators.getEmployeeAllergies({
          clientId,
          employeeId,
        }),
      ),
      put(
        actionCreators.getMedicalDetailsChangelog({
          clientId,
          employeeId,
        }),
      ),
      put(sendSuccessMessage("ALLERGY_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateAllergyError(e.response));
  }
}

export function* onRemoveAllergy({
  payload,
}: Action<EmployeeAllergyDeleteRequest>): Generator<*, void, void> {
  try {
    const clientId = (payload || {}).clientId || null;
    const employeeId = (payload || {}).employeeId || null;
    const allergyId = (payload || {}).allergyId || null;

    if (!employeeId || !clientId || !allergyId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/allergy-details/${allergyId}`,
    );
    yield all([
      put(actionCreators.toggleRemoveAllergyModal({ visible: false })),
      put(
        actionCreators.getEmployeeAllergies({
          clientId,
          employeeId,
        }),
      ),
      put(
        actionCreators.getMedicalDetailsChangelog({
          clientId,
          employeeId,
        }),
      ),
      put(actionCreators.RemoveAllergySuccess()),
      put(sendSuccessMessage("ALLERGY_REMOVED")),
    ]);
  } catch (e) {
    yield put(actionCreators.RemoveAllergyError(e.response));
  }
}

// TODO: It seems unused!
export function* onGetEmployeeMedicalHistory({
  payload,
}: Action<EmployeeMedicationsRequest>): Generator<
  *,
  void,
  $Exact<{
    data: {
      content: MedicalHistory[],
      pagination: PaginationType,
      totalElements: number,
    },
  }>,
> {
  try {
    const page = (payload || {}).page || 1;
    const clientId = (payload || {}).clientId;
    const employeeId = (payload || {}).employeeId;
    const sort = yield select(getEmployeeMedicalHistorySort);

    if (!employeeId || !clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/medical-history`,
      {
        params: {
          page,
          limit: config.shortResultsPerPage,
          sort: sortToString(sort),
        },
      },
    );

    const schema = yield parseSchema("MedicalHistoryDTO", { clientId });

    yield put(actionCreators.getEmployeeMedicalHistorySuccess(response.data));
    yield put(actionCreators.setMedicalHistorySchema(schema));
  } catch (e) {
    yield put(actionCreators.getEmployeeMedicalHistoryError(e.response));
  }
}

export function* onAddMedicalHistoryItem({
  payload,
}: Action<EmployeeMedicationsAddRequest>): Generator<*, void, void> {
  try {
    const clientId = (payload || {}).clientId;
    const employeeId = (payload || {}).employeeId;
    const data = (payload || {}).formData || {};

    if (!employeeId || !clientId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/medical-history`,
      data,
    );
    yield all([
      put(actionCreators.toggleAddMedicalHistoryItemModal(false)),
      put(
        actionCreators.getEmployeeMedicalHistory({
          clientId,
          employeeId,
        }),
      ),
      put(
        actionCreators.getMedicalDetailsChangelog({
          clientId,
          employeeId,
        }),
      ),
      put(actionCreators.AddMedicalHistoryItemSuccess()),
      put(sendSuccessMessage("HISTORY_ITEM_ADDED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddMedicalHistoryItemError(e.response));
  }
}

export function* onRemoveMedicalHistoryItem({
  payload,
}: Action<EmployeeMedicalHistoryDeleteRequest>): Generator<*, void, void> {
  try {
    const clientId = (payload || {}).clientId;
    const employeeId = (payload || {}).employeeId;
    const medicalHistoryId = (payload || {}).medicalHistoryItemId;

    if (!employeeId || !clientId || !medicalHistoryId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/medical-history/${medicalHistoryId}`,
    );
    yield all([
      put(actionCreators.toggleRemoveMedicalHistoryItemModal({ visible: false })),
      put(
        actionCreators.getEmployeeMedicalHistory({
          clientId,
          employeeId,
        }),
      ),
      put(
        actionCreators.getMedicalDetailsChangelog({
          clientId,
          employeeId,
        }),
      ),
      put(actionCreators.RemoveMedicalHistoryItemSuccess()),
      put(sendSuccessMessage("HISTORY_ITEM_REMOVED")),
    ]);
  } catch (e) {
    yield put(actionCreators.RemoveMedicalHistoryItemError(e.response));
  }
}

export function* onGetMedicalDetailsChangelog({
  payload,
}: Action<EmployeeMedicationsRequest>): Generator<
  *,
  void,
  $Exact<{
    data: {
      content: ChangelogType[],
      pagination: PaginationType,
      totalElements: number,
    },
  }>,
> {
  try {
    const clientId = (payload || {}).clientId;
    const employeeId = (payload || {}).employeeId;
    const sort = yield select(getChangelogSort);
    const page = (payload || {}).page || 1;

    if (!employeeId || !clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/medical-details/changelog`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
          sort: sortToString(sort),
        },
      },
    );
    yield put(actionCreators.getMedicalDetailsChangelogSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.getMedicalDetailsChangelogError(e.response));
  }
}

export function* onGetVaccineTypes(): Generator<*, void, *> {
  try {
    const response = yield call(http.get, `api/v1/data/vaccine`);
    yield put(actionCreators.GetVaccineTypesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetVaccineTypesError(e.response));
  }
}

export function* onGetEmployeeVaccinations({
  payload,
}: Action<{
  clientId: number,
  employeeId: number,
}>): Generator<*, void, *> {
  try {
    const { employeeId, clientId } = payload || {};
    if (!employeeId || !clientId) {
      throw Error("Invalid payload provided");
    }
    const response = yield call(
      http.get,
      `api/v1/employee-admin/client/${clientId}/employee/${employeeId}/vaccinations`,
    );
    yield put(actionCreators.GetEmployeeVaccinationsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeVaccinationsError(e.response));
  }
}

export function* onAddEmployeeVaccination({
  payload,
}: Action<{
  clientId: number,
  employeeId: number,
  formData: Object,
}>): Generator<*, void, *> {
  try {
    const { employeeId, clientId, formData } = payload || {};
    if (!employeeId || !clientId) {
      throw Error("Invalid payload provided");
    }
    yield call(
      http.post,
      `api/v1/employee-admin/client/${clientId}/employee/${employeeId}/vaccinations`,
      { ...formData },
    );
    yield all([
      put(actionCreators.GetEmployeeVaccinations({ employeeId, clientId })),
      put(actionCreators.ToggleAddVaccinationModal()),
      put(actionCreators.AddEmployeeVaccinationSuccess()),
    ]);
  } catch (e) {
    yield put(actionCreators.AddEmployeeVaccinationError(e.response));
  }
}

export function* onUpdateEmployeeVaccination({
  payload,
}: Action<{
  clientId: number,
  employeeId: number,
  vaccinationId: number,
  formData: Object,
}>): Generator<*, void, *> {
  try {
    const { employeeId, clientId, vaccinationId, formData } = payload || {};
    if (!employeeId || !clientId || !vaccinationId) {
      throw Error("Invalid payload provided");
    }
    yield call(
      http.put,
      `api/v1/employee-admin/client/${clientId}/employee/${employeeId}/vaccinations/${vaccinationId}`,
      { ...formData },
    );
    yield all([
      put(actionCreators.GetEmployeeVaccinations({ employeeId, clientId })),
      put(actionCreators.ToggleAddVaccinationModal()),
      put(actionCreators.AddEmployeeVaccinationSuccess()),
    ]);
  } catch (e) {
    yield put(actionCreators.AddEmployeeVaccinationError(e.response));
  }
}

export function* onDeleteEmployeeVaccination({
  payload,
}: Action<{
  clientId: number,
  employeeId: number,
  vaccinationId: number,
}>): Generator<*, void, *> {
  try {
    const { employeeId, clientId, vaccinationId } = payload || {};
    if (!employeeId || !clientId || !vaccinationId) {
      throw Error("Invalid payload provided");
    }
    yield call(
      http.delete,
      `api/v1/employee-admin/client/${clientId}/employee/${employeeId}/vaccinations/${vaccinationId}`,
    );
    yield all([
      put(actionCreators.GetEmployeeVaccinations({ employeeId, clientId })),
      put(actionCreators.DeleteEmployeeVaccinationSuccess()),
      put(actionCreators.ToggleDeleteVaccinationModal()),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteEmployeeVaccinationError(e.response));
  }
}

export function* MedicalDetailsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetEmployeeMedications, onGetEmployeeMedications),
    takeEvery(SetEmployeeMedicationSort, onGetEmployeeMedications),
    takeEvery(AddMedication, onAddMedication),
    takeEvery(UpdateMedication, onUpdateMedication),
    takeEvery(RemoveMedication, onRemoveMedication),
    takeEvery(GetEmployeeAllergies, onGetEmployeeAllergies),
    takeEvery(SetEmployeeAllergySort, onGetEmployeeAllergies),
    takeEvery(AddAllergy, onAddAllergy),
    takeEvery(UpdateAllergy, onUpdateAllergy),
    takeEvery(RemoveAllergy, onRemoveAllergy),
    takeEvery(GetEmployeeMedicalHistory, onGetEmployeeMedicalHistory),
    takeEvery(SetEmployeeMedicalHistorySort, onGetEmployeeMedicalHistory),
    takeEvery(AddMedicalHistoryItem, onAddMedicalHistoryItem),
    takeEvery(RemoveMedicalHistoryItem, onRemoveMedicalHistoryItem),
    takeEvery(GetMedicalDetailsChangelog, onGetMedicalDetailsChangelog),
    takeEvery(GetVaccineTypes, onGetVaccineTypes),
    takeEvery(GetEmployeeVaccinations, onGetEmployeeVaccinations),
    takeEvery(AddEmployeeVaccination, onAddEmployeeVaccination),
    takeEvery(UpdateEmployeeVaccination, onUpdateEmployeeVaccination),
    takeEvery(DeleteEmployeeVaccination, onDeleteEmployeeVaccination),
  ]);
}

export default MedicalDetailsSagas;
